import {ModelType} from "~/enums/modelType";
import IRootBuilderModel from "~/interfaces/builderModelInterfaces";
import {ICapable} from "~/interfaces/capableInterfaces";
import {IHasParent, ITrackedModel} from "~/interfaces/modelInterfaces";
import {
  IProcessFlow,
  IProcessFlowStep,
  IProcessFlowStepAction,
  IProcessFlowVariable,
  TProcessFlowStepSpecific,
} from "~/interfaces/processFlowInterfaces"
import IRepo from "~/interfaces/repo";
import Capable from "~/mixins/capable";
import Shareable from "~/mixins/shareable";
import ErrorHandler from "~/models/errorHandling/errorHandler";
import ObjectModel from "~/models/objectModel";
import Organization from "~/models/organization";
import ProcessFlowStep from "~/models/processFlows/processFlowStep";
import ProcessFlowVariable from "~/models/processFlows/processFlowVariable";
import {RelationType} from "~/models/relationType";
import ProcessFlowBuilderModel from "~/models/tracked/processFlowBuilderModel";
import TrackedModel from "~/models/tracked/trackedModel";
import ProcessFlowProvider from "~/providers/processFlowProvider";
import {IPeekerOptions} from "~/services/peeker";
import Repo from "~/state/modules/repo";
import store from "~/state/store";
import Folder from "../../../formability/document_management/models/folder";
import Form from "../../../formability/form_builder/models/form";
import ProcessFlowStepFactory from '~/models/processFlows/processFlowStepFactory'
import Validatable from '~/mixins/validatable'
import ProcessFlowBuilderValidation from '~/models/navigableValidations/processFlowBuilderValidation'
import PublishedProcessFlowDecorator from '~/models/decorators/publishedProcessFlowDecorator'
import {GenerateDocumentStep} from "~/models/processFlows/steps/generateDocumentStep";
import CertificateDynamicInput from "../../../formability/certificateBuilder/models/certificateDynamicInput";
import Share from '~/models/share'
import ProcessFlowStepContainer from "~/mixins/processFlowStepContainer"
import ProcessFlowVariableContainer from '~/mixins/processFlowVariableContainer'
import DetailView from "~/models/detailView/detailView"
import ListView from "~/models/listViews/listView"
import Certificate from "../../../formability/certificateBuilder/models/certificate";
import OnProcessFlowErrorable from '~/mixins/onProcessFlowErrorable';

export default class ProcessFlow extends OnProcessFlowErrorable(ProcessFlowVariableContainer(ProcessFlowStepContainer(Validatable(Shareable(Capable(ProcessFlowBuilderModel)))))) implements IRootBuilderModel, ICapable, IProcessFlow {
  title: string
  reference_name: string
  flow = this
  _publishedRecord: PublishedProcessFlowDecorator;
  locked_by: string
  attempted_to_publish?: boolean;
  user_interactions_enabled: boolean
  is_published: boolean

  get processFlow(): ProcessFlow {
    return this
  }

  constructor() {
    super();

    this.defineProperty('reference_name', null);
  }

  // ******************** RELATIONS AND REPOS******************** //

  static repo: IRepo<ProcessFlow, ProcessFlowProvider> = new Repo<ProcessFlow, ProcessFlowProvider>('processFlows', ProcessFlow, {}, new ProcessFlowProvider(ProcessFlow, 'process_flow', 'process_flows'));
  repo: IRepo<this, ProcessFlowProvider<this>>;

  get provider(): this['repo']['provider'] {
    return this.repo.provider;
  }

  addToRepo(): void {
    ProcessFlow.repo.add(this)
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      ...super.EMBEDDED_MODELS,
      variables: {type: ProcessFlowVariable, relationType: RelationType.dictionary},
      steps: {type: ProcessFlowStep, relationType: RelationType.dictionary},
      shares: {type: Share, relationType: RelationType.collection},
    }
  }

  createEmbeddedObject = {
    ...this.createEmbeddedObject,
    variables(parent, json): ProcessFlowVariable {
      return ProcessFlowVariable.repo.findOrCreate(json.client_id, json.id)
    },
    steps(parent, json): ProcessFlowStep {
      return ProcessFlowStepFactory.findOrCreate([json.client_id, json.id], parent, json.step_type, json)
    },
  };

  static get displayName() {
    return "Process Flow";
  }

  get collectionKey() {
    return "process_flows";
  }

  get rootName() {
    return "processFlow";
  }

  get storeName(): string {
    return `${this.rootName}Store`;
  }

  get builderStoreName(): string {
    return this.storeName;
  }

  _classKey = 'processFlow'

  get classKey(): string {
    return this._classKey;
  }

  _organization: Organization;
  folder_id: string;

  set organization(val: Organization) {
    this._organization = val;
  }

  get organization(): Organization {
    if (this._organization) {
      // for TrackedModel implementation
      return this._organization;
    } else if (this.parent instanceof Organization) {
      // for OrganizationDocument implementation
      return this.parent;
    } else {
      return this.parent.organization;
    }
  }

  updatePositionsForParent(parent: IProcessFlow, item: IProcessFlowVariable, position: number): void {
    this.variablesHandler.updateElementPositions(parent, item, position);
  }

  nextSortOrderFor(prop: string): number {
    if (prop !== 'variables') return super.nextSortOrderFor(prop);

    return this.variablesHandler.nextSortOrder();
  }

  initializeEmbedded(parent: IProcessFlow & ITrackedModel, item: (IProcessFlowVariable | IProcessFlowStep) & IHasParent<IProcessFlow>, position: number = null, json: object = {}, selectItem: boolean = true) {
    item.triggerChanges = true
    item.parent = parent
    if ('sort_order' in item) {
      item.sort_order = position ?? this.nextSortOrderFor(item.rootName)
    }
    item.startTracking()
    this.placeInDictionary(item, item.rootName, this)
    // this.updatePositionsForParent(parent, item, position);
    parent.trackCreationDeletion(item.client_id, null, item.asJson(), item);
    this.saveChangesToStore();
    return item;
  }

  // ******************** RELATIONS AND REPOS END******************** //

  get titleOrDefault(): string {
    if (this.title && this.title.length > 0) {
      return this.title;
    } else {
      return "Untitled Process Flow";
    }
  }

  // ******************** TRACK CHANGES******************** //

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.queueSendChangesToServer(this.lastPeekerOptions || {
        area: this.currentBuilderArea(),
        modelId: this.clientIdOrId
      });
      // if (this.attempted_to_publish) {
      //   _.debounce(this.validate, 250);
      // }
    }
    this._hasChanged = newVal;
  }

  saveToServer(): Promise<any> {
    return store.dispatch(`${this.builderStoreName}/saveToServer`).then(res => {
      if (res.success) {
        const processFlowJson = res.process_flow;
        const steps = processFlowJson?.steps || [];
        const variables = processFlowJson?.variables || [];

        // Throughout the process flow step configuration
        // logic we check for the presence of an id
        // to determine if the step is persisted: `step.isPersisted`.
        //
        // If we are creating a new step via the update call,
        // let's update the id for steps that saved for the first time on the backend

        const setSavedStepIds = (stepsArr) => {
          stepsArr.forEach(step => {
            const repoStep = ProcessFlowStep.repo.findById(step.client_id);
            if (!repoStep.id && step.id) {
              repoStep.loadFromJson({id: step.id})
            }
            const nestedSteps = step.steps || []
            if (nestedSteps.length) setSavedStepIds(nestedSteps)
          })
        }

        setSavedStepIds(steps)

        variables.forEach(variable => {
          const repoVariable = ProcessFlowVariable.repo.findById(variable.client_id)
          if (repoVariable) repoVariable.id = variable.id
        })
      }
      this.handleErrorsAndUpdateSaveMessage(res.better_errors)
    }).catch(err => {
      ErrorHandler.autosaveCatch(this, err);
    });
  }

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions = {
    area: this.currentBuilderArea(),
    modelId: this.clientIdOrId
  }): void {
    store.commit(`${this.builderStoreName}/saveChanges`, {changes, peekerOptions});
  }

  // ******************** TRACK CHANGES END ******************** //

  get isArchive(): boolean {
    return this.parent instanceof Folder && (this.parent.name === 'Archive' || this.parent.name === 'Trash');
  }

  canMoveTo(folder: Folder): boolean {
    return !folder.isArchiveFolder();
  }

  _moving: boolean = false
  get moving(): boolean {
    return this._moving
  }

  set moving(val: boolean) {
    this._moving = val
  }

  fetchVariableReferences({skipExisting}: { skipExisting?: boolean }): Promise<any> {
    const searches: {
      [key in IProcessFlowVariable["variable_type"] | IProcessFlowVariable['reference_scope_type']]?: {
        model: typeof TrackedModel,
        set: Set<string>,
        method: keyof ProcessFlowProvider['routes'],
        rootKey: string,
      }
    } = {
      [ModelType.Form]: {
        model: Form,
        set: new Set(),
        method: 'searchForms',
        rootKey: 'forms',
      },
      [ModelType.ObjectModel]: {
        model: ObjectModel,
        set: new Set(),
        method: 'searchObjectModels',
        rootKey: 'object_models',
      },
      [ModelType.CertificateDynamicInput]: { // for "Document Dynamic Input" variables
        model: Certificate,
        set: new Set(),
        method: 'searchCertificates',
        rootKey: 'certificates',
      },
    };
    searches[ModelType.Certificate] = searches[ModelType.CertificateDynamicInput]; // for sub-object variables

    this.variablesArray.forEach((v: IProcessFlowVariable) => {
      if (v.referenceRequired && v.reference_id && !(v.referenceModel && skipExisting)) {
        if (searches[v.variable_type]) searches[v.variable_type].set.add(v.reference_id)
      }
      if (v.referenceScopeRequired && v.reference_scope_id && !(v.referenceScopeModel && skipExisting)) {
        if (searches[v.reference_scope_type]) searches[v.reference_scope_type].set.add(v.reference_scope_id)
      }
    });

    let promises = []
    for (const key in searches) {
      const search = searches[key]
      if (search.set.size) {

        const initialSearch = this.repo.provider[search.method](this, {
          filters: {
            id: Array.from(search.set)
          }
        }).then(([data]) => {
          if (data.success) {
            search.model.repo.addManyFromJson(data[search.rootKey]);
          }
        })

        promises = promises.concat(initialSearch)

        if (search.model === ObjectModel) {
          // we need to rethink how the front end is doing this
          // it's going to end up fetching the same object model multiple times throughout
          // the course of this method with any relatively complex process flow
          const relationSearches = []
          const omIds = Array.from(search.set)

          omIds.forEach((id: string) => {
            if (search.model === ObjectModel) {
              relationSearches.push(
                this.repo.provider.fetchObjectModelRelations({objectModelId: id}).then(([supplementalData]) => {
                  ObjectModel.repo.addManyFromJson(supplementalData.object_models);
                })
              )
            }
          })
          promises = promises.concat(relationSearches)
        }


      }
    }
    return Promise.all(promises)
  }

  loadObjectModelLinkedForms() {
    const formsJson = ObjectModel.repo.all().reduce((arr, objectModel) => {
      if (objectModel.object_model_linked_forms) arr.push(...objectModel.object_model_linked_forms);

      return arr;
    }, []);

    formsJson.forEach(json => {
      if (Form.repo.findById(json.client_id)) return;

      Form.repo.add(new Form().loadFromJson(json));
    });
  }

  fetchStepReferences({skipExisting}: { skipExisting?: boolean }): Promise<any> {
    const searches: {
      [key in ModelType.ProcessFlow | ModelType.ContentDetailView | ModelType.ContentListView | ModelType.Form]?: {
        model: typeof TrackedModel,
        set: Set<string>,
        method: keyof ProcessFlowProvider['routes'],
        rootKey: string,
      }
    } = {
      [ModelType.ProcessFlow]: {
        model: ProcessFlow,
        set: new Set(),
        method: 'searchProcessFlows',
        rootKey: 'process_flows',
      },
      [ModelType.ContentDetailView]: {
        model: DetailView,
        set: new Set(),
        method: 'searchDetailViews',
        rootKey: 'detail_views',
      },
      [ModelType.ContentListView]: {
        model: ListView,
        set: new Set(),
        method: 'searchListViews',
        rootKey: 'list_views',
      },
      [ModelType.Form]: {
        model: Form,
        set: new Set(),
        method: 'searchForms',
        rootKey: 'forms',
      },
    }

    this.stepsHandler.forEach(s => {
      const step: TProcessFlowStepSpecific = s as TProcessFlowStepSpecific
      if ('allProcessFlowIds' in step) {
        step.allProcessFlowIds.forEach(id => searches[ModelType.ProcessFlow].set.add(id))
      }
      if ('allDetailViewIds' in step) {
        step.allDetailViewIds.forEach(id => searches[ModelType.ContentDetailView].set.add(id))
      }
      if ('allListViewIds' in step) {
        step.allListViewIds.forEach(id => searches[ModelType.ContentListView].set.add(id))
      }
      if ('allFormIds' in step) {
        step.allFormIds.forEach(id => searches[ModelType.Form].set.add(id))
      }
    }, {recurse: true});

    let promises = []
    for (const key in searches) {
      const search = searches[key]
      if (search.set.size) {

        const initialSearch = this.repo.provider[search.method](this, {
          filters: {
            id: Array.from(search.set)
          }
        }).then(([data]) => {
          if (data.success) {
            search.model.repo.addManyFromJson(data[search.rootKey]);
          }
        })

        promises = promises.concat(initialSearch)
      }
    }
    return Promise.all(promises)
  }

  initializeDynamicInputReferences(): void {
    this.stepsArray.forEach(step => {
      if (step instanceof GenerateDocumentStep) {
        CertificateDynamicInput.repo.addManyFromJson(step.document.certificate_dynamic_inputs);
      }
    });
  }

  get inputVariables(): IProcessFlowVariable[] {
    return this.variablesArray.filter(v => v.is_input);
  }

  get outputVariables(): IProcessFlowVariable[] {
    return this.variablesArray.filter(v => v.is_output);
  }

  /**
   * The +afterStep+ will be a step or a step action and and both will have a +jump_to+ as well as the conditional logic
   * The function figures out where to link the +newStep+, to an action +jump_to+ or a step +jump_to+ based on the
   * incoming +afterStep+
   */
  linkSteps({newStep, afterStep}: {newStep: IProcessFlowStep, afterStep: IProcessFlowStep | IProcessFlowStepAction}) {
    let nextStepClientId = null
    if (afterStep) {
      if ('actionsEnabled' in afterStep && afterStep.actionsEnabled) {
        nextStepClientId = afterStep.defaultAction.jump_to.to_client_id
        afterStep.defaultAction.jump_to.to_client_id = newStep.client_id
      } else {
        nextStepClientId = afterStep.jump_to.to_client_id
        afterStep.jump_to.to_client_id = newStep.client_id
      }
    }
    if (newStep.actionsEnabled) {
      newStep.defaultAction.jump_to.to_client_id = nextStepClientId
    } else {
      newStep.jump_to.to_client_id = nextStepClientId
    }
  }

  // ******************** VALIDATABLE******************** //
  _validation: ProcessFlowBuilderValidation = new ProcessFlowBuilderValidation(this);

  getDisplayName(propName: string, value?): string {
    return propName
  }
  // ******************** VALIDATABLE END******************** //

  // ******************** PUBLISHABLE ******************** //
  async fetchPublishedRecord(): Promise<any> {
    // Not Implemented
  }

  buildPublishedRecord(json: object = {}): this["_publishedRecord"] {
    this._publishedRecord = new PublishedProcessFlowDecorator(json)
    return this.publishedRecord
  }

  get publishedRecord(): this["_publishedRecord"] {
    return this._publishedRecord
  }

  // ******************** PUBLISHABLE END******************** //

  get canContainFinalStep(): boolean {
    return true
  }
}